import { useState } from 'react'
import './DatabaseBackup.css'

interface DatabaseBackupProps {}

function DatabaseBackup({}: DatabaseBackupProps) {
  const [backingUp, setBackingUp] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const handleBackup = async () => {
    try {
      setBackingUp(true)
      setMessage(null)

      const response = await fetch('/api/database-backup', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Ошибка при создании бэкапа')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `backup_intranet_${new Date().toISOString().split('T')[0]}_${Date.now()}.sql`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setMessage({ type: 'success', text: 'Бэкап успешно создан и загружен' })
    } catch (error: any) {
      console.error('Backup error:', error)
      setMessage({ type: 'error', text: error.message || 'Ошибка при создании бэкапа' })
    } finally {
      setBackingUp(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.name.endsWith('.sql')) {
        setSelectedFile(file)
        setMessage(null)
      } else {
        setMessage({ type: 'error', text: 'Пожалуйста, выберите SQL файл (.sql)' })
        setSelectedFile(null)
      }
    }
  }

  const handleRestore = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Пожалуйста, выберите файл для восстановления' })
      return
    }

    if (!confirm('ВНИМАНИЕ: Восстановление базы данных перезапишет все текущие данные. Продолжить?')) {
      return
    }

    try {
      setRestoring(true)
      setMessage(null)

      const formData = new FormData()
      formData.append('backup_file', selectedFile)

      const response = await fetch('/api/database-restore', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Ошибка при восстановлении базы данных')
      }

      const result = await response.json()
      setMessage({ type: 'success', text: result.message || 'База данных успешно восстановлена' })
      setSelectedFile(null)
      
      // Очищаем input
      const fileInput = document.getElementById('backup-file-input') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } catch (error: any) {
      console.error('Restore error:', error)
      setMessage({ type: 'error', text: error.message || 'Ошибка при восстановлении базы данных' })
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div className="database-backup-container">
      <h2>Управление бэкапом базы данных</h2>
      
      <div className="backup-section">
        <h3>Создать бэкап</h3>
        <p className="section-description">
          Создайте резервную копию всей базы данных. Файл будет автоматически загружен на ваш компьютер.
        </p>
        <button
          className="backup-button"
          onClick={handleBackup}
          disabled={backingUp}
        >
          {backingUp ? 'Создание бэкапа...' : '💾 Создать бэкап'}
        </button>
      </div>

      <div className="restore-section">
        <h3>Восстановить из бэкапа</h3>
        <p className="section-description warning">
          ⚠️ ВНИМАНИЕ: Восстановление базы данных полностью перезапишет все текущие данные. 
          Убедитесь, что у вас есть актуальный бэкап перед восстановлением.
        </p>
        <div className="file-input-container">
          <input
            id="backup-file-input"
            type="file"
            accept=".sql"
            onChange={handleFileSelect}
            className="file-input"
          />
          {selectedFile && (
            <div className="selected-file">
              <span>Выбранный файл: {selectedFile.name}</span>
              <span className="file-size">
                ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
          )}
        </div>
        <button
          className="restore-button"
          onClick={handleRestore}
          disabled={restoring || !selectedFile}
        >
          {restoring ? 'Восстановление...' : '🔄 Восстановить базу данных'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  )
}

export default DatabaseBackup

